// SCR-STAFF-954 — گزارش مصرف و تحویل (US-MSG-072، AC-MSG-072.1؛ AP-MSG-02، msg.report.view): به تفکیک روز، هدف، قالب، کانال و شعبه — تعداد،
// بخش پیامک، هزینه (بخش اعتبار پیامک کسرشده)، نرخ تحویل (ایمیل سنجیده نمی‌شود) و ردشده با علت؛ بازه حداکثر ۹۲ روز (PHX-MSG-0801).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type UsageReportRow } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  JalaliDatePickerRHF,
  KeyValueList,
  toBusinessDate,
  type DataTableColumn,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { msgKeys, usePurposeLabel, useReasonLabel, useTemplateLabel } from './common';

const CHANNELS = ['', 'sms', 'email', 'push'] as const;
const PURPOSES = ['', 'Otp', 'Transactional', 'Marketing', 'Internal', 'Critical'] as const;

interface RangeForm {
  from: string;
  to: string;
}

function percent(value: number | string | null | undefined): string | null {
  return value === null || value === undefined ? null : `${faDigits((Number(value) * 100).toFixed(1))}%`;
}

export function UsageReportPanel() {
  const { t } = useTranslation('msg');
  const { tenantApi, session } = useTenantSession();
  const purposeLabel = usePurposeLabel();
  const templateLabel = useTemplateLabel();
  const reasonLabel = useReasonLabel();
  const today = toBusinessDate(new Date());
  const form = useForm<RangeForm>({ defaultValues: { from: today, to: today } });
  const [range, setRange] = useState<RangeForm>({ from: today, to: today });
  const [channel, setChannel] = useState<string>('');
  const [purpose, setPurpose] = useState<string>('');
  const report = useQuery({
    queryKey: msgKeys.usage(session?.tenantId, range.from, range.to, channel, purpose),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/messaging/reports/usage', {
          params: {
            query: {
              from: range.from,
              to: range.to,
              channel: channel || undefined,
              purpose: purpose || undefined,
            },
          },
        }),
      ),
    retry: false,
  });

  const rate = (value: number | string | null) =>
    percent(value) ?? (
      <Typography variant="caption" color="text.secondary">
        {t('msg:usage.rate_na')}
      </Typography>
    );
  const columns: DataTableColumn<UsageReportRow>[] = [
    { id: 'day', headerKey: 'msg:usage.columns.day', cell: (row) => <JalaliDate value={row.day} /> },
    { id: 'purpose', headerKey: 'msg:usage.columns.purpose', cell: (row) => purposeLabel(row.purpose) },
    {
      id: 'template',
      headerKey: 'msg:usage.columns.template',
      cell: (row) => templateLabel(row.templateCode),
    },
    {
      id: 'channel',
      headerKey: 'msg:usage.columns.channel',
      cell: (row) => t(`msg:channel.${row.channel}`, { defaultValue: row.channel }),
    },
    {
      id: 'branch',
      headerKey: 'msg:usage.columns.branch',
      cell: (row) => row.branchName ?? t('msg:usage.no_branch'),
    },
    { id: 'count', headerKey: 'msg:usage.columns.count', cell: (row) => faDigits(String(row.count)) },
    { id: 'parts', headerKey: 'msg:usage.columns.parts', cell: (row) => faDigits(String(row.parts)) },
    { id: 'cost', headerKey: 'msg:usage.columns.cost', cell: (row) => faDigits(String(row.cost)) },
    { id: 'rate', headerKey: 'msg:usage.columns.rate', cell: (row) => rate(row.deliveryRate) },
    {
      id: 'suppressed',
      headerKey: 'msg:usage.columns.suppressed',
      cell: (row) =>
        Number(row.suppressed) > 0 ? (
          <span
            title={row.suppressedReasons
              .map((item) => `${reasonLabel(item.reason)}: ${faDigits(String(item.count))}`)
              .join(' · ')}
          >
            {faDigits(String(row.suppressed))}
          </span>
        ) : (
          faDigits('0')
        ),
    },
  ];
  const totals = report.data?.totals;

  return (
    <Stack spacing={2} data-testid="usage-report">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
        <JalaliDatePickerRHF control={form.control} name="from" labelKey="msg:usage.from" />
        <JalaliDatePickerRHF control={form.control} name="to" labelKey="msg:usage.to" />
        <TextField
          select
          size="small"
          label={t('msg:usage.channel')}
          value={channel}
          onChange={(event) => setChannel(event.target.value)}
          sx={{ minWidth: 140 }}
          slotProps={{ htmlInput: { 'data-testid': 'usage-filter-channel' } }}
        >
          {CHANNELS.map((value) => (
            <MenuItem key={value || 'all'} value={value}>
              {value ? t(`msg:channel.${value}`) : t('msg:usage.all')}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label={t('msg:usage.purpose')}
          value={purpose}
          onChange={(event) => setPurpose(event.target.value)}
          sx={{ minWidth: 160 }}
        >
          {PURPOSES.map((value) => (
            <MenuItem key={value || 'all'} value={value}>
              {value ? purposeLabel(value) : t('msg:usage.all')}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          onClick={form.handleSubmit((values) => setRange({ from: values.from, to: values.to }))}
          data-testid="usage-run"
        >
          {t('msg:usage.run')}
        </Button>
      </Stack>
      {report.error ? <ErrorState error={report.error} compact /> : null}
      {totals ? (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="usage-totals" data-count={String(totals.count)}>
          <KeyValueList
            columns={3}
            items={[
              { labelKey: 'msg:usage.totals.count', value: faDigits(String(totals.count)) },
              { labelKey: 'msg:usage.totals.parts', value: faDigits(String(totals.parts)) },
              {
                labelKey: 'msg:usage.totals.cost',
                value: <span data-testid="usage-cost">{faDigits(String(totals.cost))}</span>,
              },
              { labelKey: 'msg:usage.totals.delivered', value: faDigits(String(totals.delivered)) },
              {
                labelKey: 'msg:usage.totals.rate',
                value: <span data-testid="usage-rate">{rate(totals.deliveryRate)}</span>,
              },
              { labelKey: 'msg:usage.totals.suppressed', value: faDigits(String(totals.suppressed)) },
            ]}
          />
          {report.data && report.data.suppressedReasons.length > 0 ? (
            <Typography variant="body2" sx={{ mt: 1 }} data-testid="usage-reasons">
              {t('msg:usage.reasons')}:{' '}
              {report.data.suppressedReasons
                .map((item) => `${reasonLabel(item.reason)} (${faDigits(String(item.count))})`)
                .join(' · ')}
            </Typography>
          ) : null}
        </Paper>
      ) : null}
      <Alert severity="info">{t('msg:usage.cost_hint')}</Alert>
      <DataTable
        captionKey="msg:usage.caption"
        columns={columns}
        rows={report.data?.rows}
        getRowId={(row) =>
          `${row.day}|${row.purpose}|${row.templateCode}|${row.channel}|${row.branchId ?? '-'}`
        }
        isLoading={report.isPending}
        error={null}
        onRetry={() => void report.refetch()}
        emptyTitleKey="msg:usage.empty"
      />
    </Stack>
  );
}
